import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Editor, Range } from '@tiptap/react';

import { CommandMenuHandle } from '@/components/ui/command-menu-clean';
import { useRecentPrompts } from '@/store/prompts';

import { insertPromptChip } from './insertSlashCommand';
import {
  PromptCommandMenu,
  PromptMenuHeader,
  PromptMenuSection,
} from './PromptCommandMenu';
import { needsFill, templateChip } from './promptFill';
import { previewOf, splitPrefix } from './promptPreview';
import {
  PromptTemplateChip,
  RefinedSuggestionItem,
  RefinedSuggestionItemGroup,
} from './SlashExtension.types';
import { PromptFill } from './usePromptFillStep';
import { usePromptFillContext } from './usePromptSubject';

/**
 * What `useSuggestions` hands the list: the rows, plus the agent and the
 * switch, so the list can add the caller's Recent row (#596).
 */
export type PromptMenuItems = RefinedSuggestionItemGroup & {
  agentId?: number;
  templatesOn?: boolean;
};

const RECENT_KEY = 'recent-';

type CommandListProps = {
  editor: Editor;
  items: PromptMenuItems;
  command: (item: { command: RefinedSuggestionItem['command'] }) => void;
};

export type CommandListHandle = {
  onKeyDown: (data: { event: KeyboardEvent }) => boolean;
};

/**
 * The `/` popover: Command Menu with its query taken from the editor. A
 * template with `{variables}` opens its fill step in the same card first;
 * the `/` text stays until the chip lands, so "back" finds the list as it was.
 */
export const CommandList = forwardRef(
  (
    { editor, items: itemGroup, command }: CommandListProps,
    ref: ForwardedRef<CommandListHandle>
  ) => {
    const menuRef = useRef<CommandMenuHandle>(null);
    const {
      items,
      groupLabel,
      groupIcon,
      groupLucideIcon,
      groupWorkType,
      groupHighlight,
      markQuery,
      agentId,
      templatesOn = false,
    } = itemGroup;
    // Recent (switch on): the caller's last templates for this agent, at the
    // top level only; typing filters it like the groups.
    const { data: recentTemplates } = useRecentPrompts(agentId, templatesOn);
    const recent = useMemo(() => {
      if (!templatesOn || groupLabel) return [];
      const query = markQuery.trim().toLowerCase();
      return (recentTemplates ?? []).filter(
        template => !query || template.title.toLowerCase().includes(query)
      );
    }, [templatesOn, groupLabel, markQuery, recentTemplates]);
    // A template with a Context Variable always opens its fill step (#632).
    const fillContext = usePromptFillContext();
    const [filling, setFilling] = useState<{
      key: string;
      chip: PromptTemplateChip;
    } | null>(null);

    // Switch off keeps today's keys: Enter does nothing until ↑ or ↓ picks a
    // row, and the first press picks the first row. A new list starts again.
    // Switch on: Enter takes the highlighted row, the first by default.
    const pickedRef = useRef(false);
    const templatesOnRef = useRef(templatesOn);
    const rowsKey = items.map(item => item.key).join('|');
    useLayoutEffect(() => {
      pickedRef.current = false;
    }, [rowsKey]);
    useLayoutEffect(() => {
      templatesOnRef.current = templatesOn;
    }, [templatesOn]);

    useImperativeHandle(
      ref,
      () => ({
        onKeyDown: ({ event }) => {
          if (!templatesOnRef.current && !pickedRef.current) {
            if (event.key === 'Enter') return true;
            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
              pickedRef.current = true;
              return true;
            }
          }
          return menuRef.current?.keyDown(event) ?? false;
        },
      }),
      []
    );

    const sections = useMemo<PromptMenuSection[]>(
      () => [
        ...(recent.length
          ? [
              {
                key: 'prompts-recent',
                label: 'Recent',
                rows: recent.map(template => ({
                  key: `${RECENT_KEY}${template.id}`,
                  label: template.title,
                  kind: 'entry' as const,
                  recent: true,
                  mine: template.mine,
                  preview: previewOf(templateChip(template)),
                })),
              },
            ]
          : []),
        {
          key: groupLabel ? 'prompts-in-group' : 'prompt-groups',
          label: groupLabel ? '' : 'Prompts',
          rows: items.map(item => ({
            key: item.key,
            // Inside a group, the group word every title starts with reads
            // muted, so the part that tells rows apart comes first.
            ...splitPrefix(item.label, groupLabel ? groupHighlight : undefined),
            iconUrl: item.icon,
            lucideIcon: item.lucideIcon,
            workType: item.workType,
            kind: item.kind,
            mine: item.mine,
            // A title match at the top level names its group; inside a
            // group the header already does.
            groupLabel: groupLabel ? undefined : item.groupLabel,
            preview: item.kind === 'entry' ? previewOf(item.chip) : undefined,
          })),
        },
      ],
      [groupHighlight, groupLabel, items, recent]
    );

    const handlePick = useCallback(
      (key: string) => {
        if (key.startsWith(RECENT_KEY)) {
          const template = recent.find(
            candidate => `${RECENT_KEY}${candidate.id}` === key
          );
          if (!template) return;
          const chip = templateChip(template);
          if (needsFill(chip, fillContext)) {
            setFilling({ key, chip });
            return;
          }
          command({
            command: ({ editor, range }: { editor: Editor; range: Range }) =>
              insertPromptChip({ editor, chip, range }),
          });
          return;
        }
        const item = items.find(candidate => candidate.key === key);
        if (!item) return;
        if (item.kind === 'entry' && needsFill(item.chip, fillContext)) {
          setFilling({ key: item.key, chip: item.chip });
          return;
        }
        command(item);
      },
      [command, fillContext, items, recent]
    );

    // Back to the groups: keep the `/`, drop the group word and the filter.
    const handleBack = useCallback(() => {
      command({
        command: ({ editor, range }: { editor: Editor; range: Range }) => {
          editor
            .chain()
            .focus()
            .deleteRange({ from: range.from + 1, to: range.to })
            .run();
        },
      });
    }, [command]);

    const fill = useMemo<PromptFill | null>(
      () =>
        filling && {
          key: filling.key,
          mode: 'insert',
          chip: filling.chip,
          onSubmit: chip =>
            command({
              command: ({ editor, range }) =>
                insertPromptChip({ editor, chip, range }),
            }),
          // Back to the list as it was: the caret returns to the `/` text.
          onBack: () => {
            setFilling(null);
            editor.commands.focus();
          },
        },
      [command, editor, filling]
    );

    return (
      <div id='slash-extension-command-list'>
        <PromptCommandMenu
          sections={sections}
          onPick={handlePick}
          query={markQuery}
          controlRef={menuRef}
          enterHint={groupLabel ? 'to insert' : 'to open'}
          fill={fill}
          header={
            groupLabel ? (
              <PromptMenuHeader
                label={groupLabel}
                iconUrl={groupIcon}
                lucideIcon={groupLucideIcon}
                workType={groupWorkType}
                onBack={handleBack}
              />
            ) : undefined
          }
        />
      </div>
    );
  }
);
